// src/layout/MainLayout.jsx
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import Sidebar from "../components/Sidebar";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { ProfileCompletionGate } from "../components/common/ProfileCompletionModal";
import TourGuideGate from "../components/common/TourGuideGate";
import { ensureChatSocket, disconnectChatSocket } from "../services/chatSocketManager";
import { clearAllGroupPendingUploads } from "../services/groupSocketManager";
import {
    attachDurableUploadResumeHook,
    resumePersistedUploads,
} from "../services/durableMediaUpload";
import { clearPersistedUploads } from "../services/uploadPersistence";
import {
    clearStaleAuthSession,
    hasPendingVerification,
    hasUserSessionData,
    isSmsVerificationPending,
} from "../utils/authSession";
import { appStorage } from "../utils/appStorage";
import {
    isBasicPlanUser,
    logoutBasicPlanUser,
    shouldLogoutBasicPlanForRoute,
} from "../utils/basicPlanAccess";

const isPendingPayment = (value) => value === 0 || value === "0";
const isRequiredFlag = (value) => value === 1 || value === "1";

const readAuthToken = () => Cookies.get("auth_token") || "";

export default function MainLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [authToken, setAuthToken] = useState(readAuthToken);
    const [sessionOk, setSessionOk] = useState(hasUserSessionData);
    const paymentStatus = appStorage.getItem("payment_status");
    const choosePlanRequired = appStorage.getItem("choose_plan_required");
    const storedPaymentRequired =
        isPendingPayment(paymentStatus) || isRequiredFlag(choosePlanRequired);
    const [checkingPayment, setCheckingPayment] = useState(
        Boolean(authToken) && sessionOk && !storedPaymentRequired
    );
    const [serverPaymentRequired, setServerPaymentRequired] = useState(false);

    const isAuthenticated = Boolean(authToken) && sessionOk;

    useEffect(() => {
        const syncAuthState = () => {
            const nextToken = readAuthToken();
            const nextSessionOk = hasUserSessionData();

            if (nextToken && !nextSessionOk) {
                clearStaleAuthSession();
                setAuthToken("");
                setSessionOk(false);
                toast.dismiss();
                toast.error("Session expired. Please log in again.");
                return;
            }

            if (!nextToken && nextSessionOk) {
                clearStaleAuthSession();
                setAuthToken("");
                setSessionOk(false);
                return;
            }

            setAuthToken(nextToken);
            setSessionOk(nextSessionOk);
        };

        syncAuthState();
        window.addEventListener("focus", syncAuthState);
        document.addEventListener("visibilitychange", syncAuthState);
        const intervalId = window.setInterval(syncAuthState, 3000);

        return () => {
            window.removeEventListener("focus", syncAuthState);
            document.removeEventListener("visibilitychange", syncAuthState);
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        if (!isAuthenticated || storedPaymentRequired) {
            setCheckingPayment(false);
            return undefined;
        }

        let isActive = true;

        const checkPaymentStatus = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                });
                const data = await res.json();
                if (!isActive) return;

                if (!res.ok) {
                    throw new Error(data?.message || "Failed to check payment status");
                }

                const profile =
                    data?.data?.[0]?.profile ||
                    data?.data?.profile ||
                    data?.profile ||
                    data?.data?.[0] ||
                    {};
                const apiPaymentStatus =
                    profile.payment_status ?? profile.current_payment_status;
                const apiChoosePlanRequired = profile.choose_plan_required;

                if (apiPaymentStatus != null) {
                    appStorage.setItem("payment_status", apiPaymentStatus.toString());
                }
                if (apiChoosePlanRequired != null) {
                    appStorage.setItem(
                        "choose_plan_required",
                        apiChoosePlanRequired.toString()
                    );
                }

                setServerPaymentRequired(
                    isPendingPayment(apiPaymentStatus) || isRequiredFlag(apiChoosePlanRequired)
                );
            } catch (error) {
                } finally {
                if (isActive) setCheckingPayment(false);
            }
        };

        checkPaymentStatus();

        return () => {
            isActive = false;
        };
    }, [authToken, isAuthenticated, storedPaymentRequired]);

    useEffect(() => {
        if (isAuthenticated && authToken) {
            ensureChatSocket(authToken);
            attachDurableUploadResumeHook();
            resumePersistedUploads();
        } else {
            disconnectChatSocket();
            clearAllGroupPendingUploads();
            clearPersistedUploads();
        }
    }, [isAuthenticated, authToken]);

    useEffect(() => {
        if (storedPaymentRequired || serverPaymentRequired) {
            toast.dismiss();
            toast("Please complete payment to continue.");
        }
    }, [storedPaymentRequired, serverPaymentRequired]);

    useEffect(() => {
        if (!isAuthenticated) return;

        if (shouldLogoutBasicPlanForRoute(location.pathname, location.search)) {
            logoutBasicPlanUser(navigate);
        }
    }, [isAuthenticated, location.pathname, location.search, navigate]);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    if (storedPaymentRequired || serverPaymentRequired) {
        return <Navigate to="/payment" replace />;
    }

    if (isBasicPlanUser()) {
        return <Navigate to="/payment" replace />;
    }

    if (hasPendingVerification()) {
        if (isSmsVerificationPending()) {
            appStorage.setItem("verification_type", "3");
        }
        return <Navigate to="/otp" replace />;
    }

    if (checkingPayment) {
        return (
            <div className="flex min-h-[var(--app-height,100dvh)] items-center justify-center bg-[#F3F0FF] text-gray-600">
                <p className="text-sm text-gray-500">Loading…</p>
            </div>
        );
    }

    return (
        <div className="flex h-[var(--app-height,100dvh)] min-h-0 flex-col overflow-hidden bg-[#F3F0FF] max-md:fixed max-md:inset-x-0 max-md:top-[var(--vv-offset-top,0px)] max-md:w-full md:relative md:flex-row">
            <Sidebar />
            <main className="flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain bg-[#F3F0FF] [-webkit-overflow-scrolling:touch] max-md:overflow-hidden max-md:pb-[var(--mobile-nav-offset,5rem)] md:overflow-hidden md:pb-0">
                <div className="h-full min-h-0 overflow-hidden bg-[#F3F0FF]">
                    <Outlet />
                </div>
            </main>
            <TourGuideGate />
            <ProfileCompletionGate />
        </div>
    );
}
